import type { TaxaAggregationResult } from './TaxaAggregation.ts'

export interface RankedTaxon {
  name: string
  value: number
}

/**
 * Rank a single-bucket taxa aggregation by total reads and keep the top `topN`.
 *
 * Aggregation-agnostic post-step: sum each key's magnitude across the (typically one) bucket, drop
 * zero/empty entries, sort DESCENDING by reads, then slice to `topN`. The Ranked Bar sink feeds it the
 * LEAF (deepest-resolved organism) aggregation `taxaLeafAggregationSingle` — so "top N" means the N
 * most-abundant organisms — but the function works for any `TaxaAggregationResult` (e.g. the trait-group
 * aggregation in Nature-KPI mode). Kept pure + separate so it is unit-testable.
 *
 * @param agg    a taxa/trait aggregation (name → reads), already scoped/zoomed + grouped by the caller
 * @param topN   how many highest-ranked entries to keep; non-positive/undefined falls back to 10
 */
export function rankTopTaxa(agg: TaxaAggregationResult, topN?: number): RankedTaxon[] {
  const limit = topN && topN > 0 ? Math.floor(topN) : 10

  const totals: Record<string, number> = Object.fromEntries(agg.taxa.map((t) => [t, 0]))
  for (const row of agg.rows) {
    for (const [name, reads] of Object.entries(row.data)) {
      totals[name] = (totals[name] ?? 0) + (reads ?? 0)
    }
  }

  return Object.entries(totals)
    .filter(([, value]) => value > 0)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit)
}
